import React from 'react';
import {
    ShowButton,
    EditButton,
    SimpleShowLayout,
    TabbedForm,
    FormTab,
    TextField,
    BooleanField,
    TextInput,
    NumberInput,
    SelectInput,
    DisabledInput,
    minValue,
    required
} from 'admin-on-rest';
import { DependentInput } from 'aor-dependent-input';
import ShowWithType from '../admin/ShowWithType';
import CreateEditProductUnit from '../admin/CreateEditProductUnit';
import ValidatedProductTextInput from '../components/ValidatedProductTextInput';
import ProductUnitCheck from '../components/ProductUnitCheck';
import ListButton from '../buttons/ListButton';
import PriceLevelPriceTable from '../components/PriceLevelPriceTable';
import ProductByTypeCategoryDependentSelectInput from '../components/ProductByTypeCategoryDependentSelectInput';
import ProductCategoryByTypeDependentSelectInput from '../components/ProductCategoryByTypeDependentSelectInput';
import LocationPriceTable from '../components/LocationPriceTable';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import { viewStyles } from '../shared/styles-view';
import { CardActions } from 'material-ui/Card';
import Icon from 'material-ui/svg-icons/device/widgets';
import { RefreshButton } from 'admin-on-rest/lib/mui/button';
import Avatar from 'material-ui/Avatar';
import { resolveAccess } from '../shared/authFunctions';

export const fuseProductUnitIcon = Icon;

const styles = {
    inlineBlock: { flex: '1 1 40%', display: 'inline-block', width: 'auto' },
    diagramUnit: {
        display: 'inline-block',
        float: 'right',
        width: '200px',
        maxWidth: '100%',
        height: 'auto',
        margin: '2em 0 0 1em',
        clear: 'right'
    },
    rowShowField: { minWidth: 120 },
    fieldSmall: { flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden' }
};

// Create a new map of Price Level or Location Prices
const separatePrices = (priceRecord, list) => {
    let priceList = Object.entries(priceRecord);
    if (priceRecord && priceList) {
        let priceMap = new Map();
        for (let item of priceList) {
            const hasLocation = item[0].indexOf('location');
            const hasDefault = item[0].indexOf('default');
            const isHidden = item[0].indexOf('hidden');
            // For Price Level Prices, add price if it is not a Location, not Default, and not hidden (Ex: "LAFP20")
            if (list === 'price' && hasLocation === -1 && hasDefault === -1 && isHidden === -1) {
                priceMap.set(item[0], item[1]);
            }
            // For Location Prices, add price if it is a Location (Ex: "location_9999")
            if (list === 'location' && hasLocation !== -1) {
                priceMap.set(item[0], item[1]);
            }
        }
        return priceMap;
    }
    return null;
};

// Handle event from Product Unit list retrieval and default Unit
const handleProductUnitList = (list, defaultUnit) => {
    //console.log('parent Product Units: ', list);
    //console.log('parent Default Product Unit: ', defaultUnit);
};

// Handle event from item Add
const handleAddPrice = (e, index, value) => {
    //console.log('Add ' + e + ' to price');
};

// Handle event from item Delete
const handleDeletePrice = (e) => {
    // console.log('Delete ' + e + ' from price');
};

const MakePriceLevelPriceTable = ({ record, source, showButtons }) => {
    const priceList = (record.price) ? separatePrices(record.price, 'price') : new Map();
    return (
        <PriceLevelPriceTable
            {...record}
            priceLevelPriceList={priceList}
            onAddUpdate={handleAddPrice}
            onDeleteUpdate={handleDeletePrice}
            showCreate={showButtons.create}
            showDelete={showButtons.delete} />
    );
};

const MakeLocationPriceTable = ({ record, source, showButtons }) => {
    const priceList = (record.price) ? separatePrices(record.price, 'location') : new Map();
    return (
        <LocationPriceTable
            {...record}
            locationPriceList={priceList}
            onAddUpdate={handleAddPrice}
            onDeleteUpdate={handleDeletePrice}
            showCreate={showButtons.create}
            showDelete={showButtons.delete} />
    );
};

/*
 * View: Show
 */

// Set title for Show Product Unit
const ShowTitle = ({ record }) => {
    return <span><Avatar icon={<Icon />} style={viewStyles.avatar}
        className="avatar-title" />{record ? `${record.name}` : 'Product Unit'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Product Unit details below. To edit this Product Unit or its Prices,
                click <strong>Edit</strong> above.</p>
        </span>
    );
};

const ShowDescriptionViewOnly = () => {
    return (
        <span>
            <p>Review Product Unit details below.</p>
        </span>
    );
};

// Replace Actions for Show Product Unit
const ShowActions = ({ basePath, data, canEdit }) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath="/Products" label="List Products" />
        {canEdit &&
            <EditButton basePath={basePath} record={data} />
        }
        <RefreshButton />
    </CardActions>
);

/*
 * Show Product Unit Table
 */
export const fuseProductUnitShow = ({ canEdit, ...props }) => (
    <ShowWithType {...props} title={<ShowTitle />} desc={canEdit ? <ShowDescription /> : <ShowDescriptionViewOnly />}
        actions={<ShowActions canEdit={canEdit} />}>
        <SimpleShowLayout>
            <TextField source="product_id" label="Product ID"
                style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <TextField source="id" label=" Product Unit ID"
                style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <TextField source="name" label="Name" />
            <TextField source="type" label="Product Type" />
            <TextField source="brand" label="Brand" />
            <TextField source="category" label="Category" style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <TextField source="category_id" label="ID" style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <hr style={viewStyles.break} />

            <TextField source="unit_type" label="Unit Type"
                       style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <BooleanField source="default_unit" label="Unit is Default"
                style={{ ...viewStyles.rowShowField, ...styles.rowShowField }}
                elStyle={styles.fieldSmall} />
            <TextField source="piece_unit" label="Piece Unit"
                       style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <hr style={viewStyles.break} />

            <TextField source="units_per_scan" label="Units per Scan" />
            <hr style={viewStyles.break} />

            <BooleanField source="taxable" label="Taxable"
                          style={{ ...viewStyles.rowShowField, ...styles.rowShowField }}
                          elStyle={styles.fieldSmall} />
            <TextField source="cost" label="Cost" style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <TextField source="price.default" label="Default Price"
                style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
            <hr style={viewStyles.break} />

            <BooleanField source="active" label="Unit is Active"
                          style={{ ...viewStyles.rowShowField, ...styles.rowShowField }}
                          elStyle={styles.fieldSmall} />
            <hr style={viewStyles.break} />

            {<MakePriceLevelPriceTable showButtons={{ 'create': false, 'delete': false }} />}
            {<MakeLocationPriceTable showButtons={{ 'create': false, 'delete': false }} />}
        </SimpleShowLayout>
    </ShowWithType>
);

/*
 * View: Create form
 */

// Set title for Create Product Unit
const CreateTitle = ({ record }) => {
    return <span><Avatar icon={<Icon />} style={viewStyles.avatar}
        className="avatar-title" />Create a Product Unit {record ? `for ${record.name}` : ''}</span>;
};

const CreateDescription = () => {
    return (
        <span>
            <img src="img-diagram-Product-Unit-List.png" alt="Diagram of Products - Product Units"
                style={viewStyles.diagramSmall} />
            <p>A <strong>Product Unit</strong> for a Product is a physical item or set sold at your
                Location(s). They belong to a Product, which must be created before creating a Product Unit.</p>
            <p>A Product Unit can be a single sellable item (called a <em>Piece</em>) or multiple pieces bundled
                together (<em>Mid</em>, <em>Case</em>) to be sold.</p>
            <p>Enter the required fields in each Tab.</p>
        </span>
    );
};

// Replace Actions for Show Product Unit
const CreateActions = ({ basePath, data }) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath="/Products" label="Back to Products" />
        <RefreshButton />
    </CardActions>
);

// Set defaults for Create form
const defaultCreateValues = {
    active: false,
    default_unit: false,
    skus: null,
    price: { default: '0', hidden: '0.01' },
    parent_product_units: null,
    parent_default_unit: null
};

const createDisplayNoProductUnits = (productUnits) => productUnits === null;
const createDisplayProductUnitsWithNoDefault = (record) => {
    if (record) {
        return (
            record.parent_product_units !== null && record.parent_default_unit === null
        );
    }
    return false;
};
const createDisplayIfNotPieceUnit = (unitTypeValue) => unitTypeValue !== 'Piece';
const createDisplayIfPieceUnit = (record) => record.unit_type === 'Piece';
const createDisplayIfPieceUnitAndDisallowDefault = (record) => (record.unit_type === 'Piece' && record.parent_default_unit !== null);
const createDisplayIfPieceUnitAndAllowDefault = (record) => (record.unit_type === 'Piece' && (!record.parent_default_unit || record.id === record.parent_default_unit.id));
const createDisplayPrices = (defaultPrice) => defaultPrice > 0;

/*
 * Create Product Unit form (based on Edit form)
 */
export const fuseProductUnitCreate = (props) => (
    <CreateEditProductUnit {...props} title={<CreateTitle />} desc={<CreateDescription />} actions={<CreateActions />}>
        <TabbedForm defaultValue={defaultCreateValues} submitOnEnter={false}>

            {/* Create Step: Product Unit Information */}
            <FormTab label="Product Unit Information" style={viewStyles.row}>

                <ProductUnitCheck onProductUnitList={handleProductUnitList} />

                <span style={viewStyles.fieldHeader}>
                    Product Type / Product Category / Brand
                    <TooltipIcon dataFor="ttTypeCat" />
                    <ReactTooltip id="ttTypeCat" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        <span style={viewStyles.fieldHeaderDescription}>
                            A <strong>Product Type</strong> is used for configuring the POS System to display products
                            to be sold dependent on your Location's DBA. In most cases, a business will only have one
                            Product Type.
                            <br /><br />
                            <strong>Product Categories</strong> are used for sorting between Products on the POS
                            System so employees can browse quickly for the Product they need.
                            <br /><br />
                            Some Products might have the same name but are made by different companies.
                            The <strong>Brand</strong> field differentiates between different products of the same
                            name.
                            <br /><br />
                            The Product Type, Category, Product ID and Brand are already populated for this Product
                            Unit.
                        </span>
                    </ReactTooltip>
                </span>
                <TextField source="type" label="Type"
                    style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                <TextField source="category" label="Category"
                    style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                <TextField source="category_id" label="ID"
                    style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                <TextField source="brand" label="Brand"
                    style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                <hr style={viewStyles.break} />

                <TextField source="product_id" label="Product ID / Barcode #" style={viewStyles.fieldLight} />

                <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
                <span style={viewStyles.fieldDescription}>
                    Enter and verify the unique Product Unit <strong>Barcode Number</strong>.
                    <TooltipIcon dataFor="ttId" />
                    <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        Barcode numbers are used to look up Product Units on the POS System by scanning.
                        <br /><br />
                        Typically, a Product Unit's ID is similar to its parent Product, possibly with an extra
                        digit. A Product Unit is a sellable unit for a Product, with specific unit quantities.
                    </ReactTooltip>
                </span>
                <ValidatedProductTextInput
                    formName="record-form"
                    source="product_unit_id"
                    parentField="type"
                    fieldType="text"
                    label="ID*"
                    lookupType="productUnits"
                    buttonLabel="Check Availability"
                    successMessage="This Product Unit ID / Barcode is available."
                    errorDuplicateMessage="An item with this Barcode already exists"
                    errorRequiredMessage="A unique ID / Barcode is required."
                    errorValidateMessage="Please verify the availability of this ID / Barcode."
                    fieldResource="BarcodeLookup"
                    isRequired={true}
                />

                <TextInput source="name" label="Product Unit Name" validate={[required]} />
                <span style={viewStyles.fieldDescription}>
                    Update the <strong>Name</strong> for the Product Unit if it is different from its Product's Name.
                    <TooltipIcon dataFor="ttName" />
                    <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        In most cases, the <strong>Name</strong> of a Product Unit will be the same as or similar to its
                        Product. You may have an instance where a Product Unit's Name is different than its Product name.
                    </ReactTooltip>
                </span>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Unit Selection Tab to proceed.
                </span>
                <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
            </FormTab>

            {/* Create Step: Unit Selection */}
            <FormTab label="Unit Selection" style={viewStyles.row}>

                <span>
                    <img src="img-diagram-Product-Unit-Type-Create.png" alt="Diagram of Product Unit - Unit Type"
                        style={styles.diagramUnit} />
                </span>

                {/* Product's current Default Piece */}
                <DependentInput dependsOn="parent_default_unit">
                    <span style={viewStyles.fieldHeader}>
                        Current default Piece
                        <TooltipIcon dataFor="ttCurrentDefault" />
                        <ReactTooltip id="ttCurrentDefault" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            <span style={viewStyles.fieldHeaderDescription}>
                                Below is the default Product Unit (Piece) for this Product. Since a default Unit
                                already exists, this new Product Unit should not be set as the default. To change the
                                default Unit, edit the existing one.
                            </span>
                        </ReactTooltip>
                    </span>
                    <DisabledInput source="parent_default_unit.id" label="ID"
                        style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                    <DisabledInput source="parent_default_unit.name" label="Name"
                        style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                </DependentInput>
                <hr style={viewStyles.break} />

                {/* First Default Unit (if no existing Product Units) */}
                <DependentInput dependsOn="parent_product_units" resolve={createDisplayNoProductUnits}>
                    <span style={viewStyles.fieldHeader}>Create a Default Unit</span>
                    <p>The first Product Unit for a Product should be a Unit Type of "Piece", and be set as the default
                        Product Unit.</p>
                    <p>If need be, update the fields in the <strong>Product Unit Information</strong> tab.</p>
                </DependentInput>

                {/* Default Unit (if Product Units exist with no Default) */}
                <DependentInput dependsOn="parent_product_units" resolve={createDisplayProductUnitsWithNoDefault}>
                    <span style={viewStyles.fieldHeader}>Create a Default Unit</span>
                    <p>Products must have a default Product Unit Piece. This Product does not yet have a default
                        Piece, so this new Product Unit should be set to a Unit Type of "Piece" and be the default
                        Unit.</p>
                    <p>If need be, update the fields in the <strong>Product Unit Information</strong> tab.</p>
                </DependentInput>

                <hr style={viewStyles.break} />

                {/* Choose Unit Type */}
                <SelectInput source="unit_type" label="Select Unit Type"
                    validate={[required]}
                    defaultValue="Piece"
                    choices={[
                        { id: 'Piece', name: 'Piece' },
                        { id: 'Mid', name: 'Mid' },
                        { id: 'Case', name: 'Case' }
                    ]}
                />
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Unit Type</strong>. See the diagram for examples.
                    <TooltipIcon dataFor="ttUnit" />
                    <ReactTooltip id="ttUnit" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        You have the option to select <em>Piece</em>, <em>Mid</em> or <em>Case</em>. A Piece refers to
                        a single Product Unit, and should be created first before creating a Mid or Case. A Mid and Case
                        refers to multiple Pieces that are grouped together and sold.
                        <br /><br />
                        Inventory on POS is calculated by a Product's <em>Pieces</em>.
                        <ul>
                            <li>When 1 Product Piece is sold or transferred, Inventory qty for that product is
                                subtracted by 1.</li>
                            <li>If a Product Piece is Received or Returned, Inventory qty for the product is added
                                by 1.</li>
                            <li>If a Mid or Case containing 3 Product Pieces is sold or transferred, Inventory qty for
                                that Product is subtracted by 3.</li>
                            <li>If a Mid or Case containing 3 Product Pieces is received or returned, Inventory qty for
                                that Product is added by 3.</li>
                        </ul>
                        <p>See the diagram for examples of Unit Types.</p>
                    </ReactTooltip>
                </span>

                {/* Default Unit Messaging (if Piece) */}
                <DependentInput resolve={createDisplayIfPieceUnitAndAllowDefault}>
                    <SelectInput source="default_unit" label="Unit is Default"
                        validate={[required]}
                        choices={[
                            { id: true, name: 'Yes' },
                            { id: false, name: 'No' }
                        ]}
                    />

                    <span style={viewStyles.fieldDescription}>
                        Is this Unit the <strong>Default</strong> for this Product?
                        <TooltipIcon dataFor="ttDefault" />
                        <ReactTooltip id="ttDefault" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            <strong>Default Unit</strong> refers to the Product Unit Piece used in reference
                            to <em>Mids</em> and <em>Cases</em>. Only a <em>Piece</em> can be the default unit for a
                            Product, and all Products must have a default Piece Unit.
                            <br /><br />
                            If you create multiple Product Units with a Unit Type of "Piece", only one of them may be
                            the Default Unit.
                        </ReactTooltip>
                    </span>
                </DependentInput>

                <DependentInput resolve={createDisplayIfPieceUnitAndDisallowDefault}>
                    <span style={viewStyles.fieldDescription}>Because there is a Default Piece Unit, this Unit cannot be set as the default unless it is the same Unit.</span>
                </DependentInput>

                {/* Piece Unit Barcode (Mid or Case) */}
                <DependentInput dependsOn="unit_type" resolve={createDisplayIfNotPieceUnit}>
                    <TextInput source="piece_unit" label="Piece Unit Barcode" />
                    <span style={viewStyles.fieldDescription}>
                        Piece Unit barcode for <em>Mid</em> and <em>Case</em> Unit
                        Types.
                        <TooltipIcon dataFor="ttPieceUnit" />
                        <ReactTooltip id="ttPieceUnit" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            <strong>Piece Unit</strong> refers to the ID of the default Product Unit Piece that makes
                            up the content of <em>Mids</em> and <em>Cases</em>.
                            <br /><br />
                            This value may be edited, for example, to the barcode of an Alias for the Unit.
                        </ReactTooltip>
                    </span>
                </DependentInput>

                {/* Units per Scan (Piece) */}
                <DependentInput resolve={createDisplayIfPieceUnit}>
                    <TextField source="units_per_scan_piece" value={1} defaultValue={1} label="Units per Scan" />
                </DependentInput>

                {/* Units per Scan (Mid or Case) */}
                <DependentInput dependsOn="unit_type" resolve={createDisplayIfNotPieceUnit}>
                    <NumberInput source="units_per_scan" label="Units per Scan" defaultValue={1} step={1}
                        validate={[required, minValue(1, 'Must not be a negative number')]} />
                    <span style={viewStyles.fieldDescription}>
                        Enter the total of Units scanned at the register.
                        <TooltipIcon dataFor="ttUnitsPerScan" />
                        <ReactTooltip id="ttUnitsPerScan" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            The <strong>Units Per Scan</strong> sets how many pieces are included when
                            a <em>Mid</em> or a <em>Case</em> is scanned.
                        </ReactTooltip>
                    </span>
                </DependentInput>

                <SelectInput source="active" label="Display Product Unit on POS"
                    validate={[required]}
                    choices={[
                        { id: true, name: 'Yes' },
                        { id: false, name: 'No' }
                    ]} />
                <span style={viewStyles.fieldDescription}>
                    Select Yes or No.
                    <TooltipIcon dataFor="ttActive" />
                    <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        Selecting <em>Yes</em> will display the Product Unit on the POS with the ability to be sold.
                        <br />
                        Selecting <em>No</em> will remove the Product Unit from the POS so it can not be selected or sold.
                        <br /><br />
                        In some cases, you may want to create Product Units that will not be displayed because they are
                        not at your Location(s) yet. Also, a discontinued Product Unit can be removed from your
                        Location(s). This mitigates human error from having employees accidently sell Product Units
                        that are not in your Location(s).
                    </ReactTooltip>
                </span>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Prices Tab to proceed.
                </span>
                <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
            </FormTab>

            {/* Create Step: Prices */}
            <FormTab label="Prices" style={viewStyles.row}>
                <SelectInput source="taxable" label="Taxable"
                    validate={[required]}
                    choices={[
                        { id: true, name: 'Yes' },
                        { id: false, name: 'No' }
                    ]} />
                <span style={viewStyles.fieldDescription}>
                    Is tax applied to this Product Unit?
                </span>

                <TextInput source="cost" label="Cost of Unit" />
                <span style={viewStyles.fieldDescription}>
                    Enter number for <strong>Cost</strong> to manufacture this Product Unit.
                    <TooltipIcon dataFor="ttCost" />
                    <ReactTooltip id="ttCost" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        <strong>Cost</strong> of Unit is for how much the Product Unit costs the business to manufacture
                        or purchase. This helps with determining certain values for Accounting Reports.
                    </ReactTooltip>
                </span>

                <TextInput source="price.default" label="Default Price" type="number"
                    validate={[required, minValue(0.01, 'Price must be above zero.')]} />
                <span style={viewStyles.fieldDescription}>
                    Enter the <strong>Default Price</strong> for this Product Unit (must be greater than 0).
                    <TooltipIcon dataFor="ttDefaultPrice" />
                    <ReactTooltip id="ttDefaultPrice" type="light" effect="solid" multiline={true} border={true}
                        className="tooltip">
                        This <strong>Default Price</strong> determines the selling Price of the Unit. Overrides may be
                        created for specific Locations or for specific Price Levels.
                        <br /><br />
                        Once a Default Price is selected, additional Price overrides may be added.
                    </ReactTooltip>
                </span>
                <TextField source="price.hidden" style={viewStyles.hidden} elStyle={viewStyles.hidden} />


                <DependentInput dependsOn="price.default" resolve={createDisplayPrices}>
                    {<MakePriceLevelPriceTable showButtons={{ 'create': true, 'delete': true }} />}
                    {<MakeLocationPriceTable showButtons={{ 'create': true, 'delete': true }} />}
                </DependentInput>

                <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to this Product Unit's Product.
                </span>
                <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
            </FormTab>

        </TabbedForm>
    </CreateEditProductUnit>
);

/*
 * View: Edit form
 */

// Set title for Edit Product Unit
const EditTitle = ({ record }) => {
    return <span><Avatar icon={<Icon />} style={viewStyles.avatar}
        className="avatar-title" />Edit {record ? `${record.name}` : 'Product Unit'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>A <strong>Product Unit</strong> for a Product is a physical item or set sold at your
                Location(s). They belong to a Product.</p>
            <p>A Product Unit can be a single sellable item (called a <em>Piece</em>) or multiple pieces bundled
                together (<em>Mid</em>, <em>Case</em>) to be sold.</p>
            <p>Update fields in each Tab.</p>
        </span>
    );
};

// Replace Actions for Edit Product
const EditActions = ({ basePath, data }) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath="/Products" label="List Products" />
        <ShowButton basePath={basePath} record={data} />
        <RefreshButton />
    </CardActions>
);

const editDisplayProductUnitIsNotDefault = (defaultUnit) => defaultUnit === false;
const editDisplayIfPieceUnit = (unitTypeValue) => unitTypeValue === 'Piece';
const editDisplayIfNotPieceUnit = (unitTypeValue) => unitTypeValue !== 'Piece';
const editDisplayIfPieceUnitAndDisallowDefault = (record) => (record.unit_type === 'Piece' && record.parent_default_unit !== null);
const editDisplayIfPieceUnitAndAllowDefault = (record) => (record.unit_type === 'Piece' && (!record.parent_default_unit || record.id === record.parent_default_unit.id));
const editDisplayPrices = (defaultPrice) => defaultPrice > 0;

/*
 * Create / Edit Product Unit form
 */
export const fuseProductUnitEdit = (props) => (
    <CreateEditProductUnit {...props} title={<EditTitle />} desc={<EditDescription />} actions={<EditActions />}>
        {permissions =>
            <TabbedForm submitOnEnter={false}>

                {/* Edit Step: Product Unit Info */}
                <FormTab label="Product Unit Info" style={viewStyles.row}>
                    <ProductUnitCheck onProductUnitList={handleProductUnitList} />
                    <span style={viewStyles.fieldHeader}>
                        Product Type / Product Category / Brand
                        <TooltipIcon dataFor="ttTypeCat" />
                        <ReactTooltip id="ttTypeCat" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            <span style={viewStyles.fieldHeaderDescription}>
                                A <strong>Product Type</strong> is used for configuring the POS System to display products
                                to be sold dependent on your Location's DBA. In most cases, a business will only have one
                                Product Type.
                                <br /><br />
                                <strong>Product Categories</strong> are used for sorting between Products on the POS
                                System so employees can browse quickly for the Product they need.
                                <br /><br />
                                Some Products might have the same name but are made by different companies.
                                The <strong>Brand</strong> field differentiates between different products of the same
                                name.
                                <br /><br />
                                The Product Type, Category, Product ID and Brand are already populated for this Product
                                Unit. You may change these values if desired.
                            </span>
                        </ReactTooltip>
                    </span>
                    <hr style={viewStyles.break} />

                    <TextField source="type" label="Product Type" style={viewStyles.fieldLight} />
                    <hr style={viewStyles.break} />

                    <DependentInput dependsOn="type" style={viewStyles.rowField}>
                        <span style={{ ...viewStyles.fieldLabel, ...viewStyles.marginTop }}>Product Category</span>
                        <ProductCategoryByTypeDependentSelectInput
                            {...props}
                            source="category_id"
                            dependentProp="dependsOnValue"
                            optionValue="id"
                            optionText="name"
                            label="Select a Product Category"
                            validate={[required]}
                            allowEmpty
                        />
                    </DependentInput>

                    <TextInput source="brand" label="Brand" validate={[required]} />
                    <span style={viewStyles.fieldDescription}>
                        Change <strong>Brand</strong> name.
                        <TooltipIcon dataFor="ttBrand" />
                        <ReactTooltip id="ttBrand" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            Some Products might have the same name but are made by different companies. You can use
                            the <strong>Brand</strong> field to differentiate between different products of the same name.
                            <br /><br />
                            For example, Product "Banana" has a brand name of "Dole", while another Product "Banana" has a
                            brand name of "Chiquita".
                        </ReactTooltip>
                    </span>
                    <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />

                    <span style={viewStyles.fieldHeader}>
                        Product ID / Barcode #
                    </span>
                    <TextField source="current_product_id" label="Current Product ID / Barcode #"
                        style={viewStyles.fieldLight} />
                    <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />

                    <DependentInput dependsOn={['type', 'category_id']}>
                        <span style={{ ...viewStyles.fieldLabel, ...viewStyles.marginTop }}>Product</span>
                        <ProductByTypeCategoryDependentSelectInput
                            {...props}
                            source="product_id"
                            dependentProp="dependsOnValue"
                            optionValue="id"
                            optionText="name"
                            label="Change Product"
                            allowEmpty
                        />
                    </DependentInput>

                    <TextField source="id" label="Product Unit ID / Barcode #" />
                    <span style={viewStyles.fieldDescription}>
                        The unique Product Unit <strong>Barcode Number</strong>.
                        <TooltipIcon dataFor="ttId" />
                        <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            Barcode numbers are used to look up Product Units on the POS System by scanning.
                            <br /><br />
                            Typically, a Product Unit's ID is similar to its parent Product, possibly with an extra
                            digit. A Product Unit is a sellable unit for a Product, with specific unit quantities.
                        </ReactTooltip>
                    </span>

                    <TextInput source="name" label="Product Unit Name" validate={[required]} />
                    <span style={viewStyles.fieldDescription}>
                        The <strong>Name</strong> for the Product Unit if it is different from its Product Name.
                        <TooltipIcon dataFor="ttName" />
                        <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            In most cases, the <strong>Name</strong> of a Product Unit will be the same as or similar to its
                            Product. You may have an instance where a Product Unit's Name is different than its Product name.
                        </ReactTooltip>
                    </span>

                    <TextField source="skus" style={viewStyles.hidden} elStyle={viewStyles.hidden} />

                    <span style={viewStyles.fieldInstructions}>
                        Upon completing this Tab, click the Unit Selection Tab to proceed.
                    </span>
                    <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
                </FormTab>

                {/* Edit Step: Unit Selection */}
                <FormTab label="Unit Selection" style={viewStyles.row}>

                    <BooleanField source="default_unit" style={viewStyles.hidden} elStyle={viewStyles.hidden} />

                    {/* Product's current Default Piece */}
                    <DependentInput dependsOn="default_unit" resolve={editDisplayProductUnitIsNotDefault}>
                        <span style={viewStyles.fieldHeader}>
                            Current default Piece
                            <TooltipIcon dataFor="ttCurrentDefault" />
                            <ReactTooltip id="ttCurrentDefault" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                <span style={viewStyles.fieldHeaderDescription}>
                                    Below is the default Product Unit (Piece) for this Product. Since a default Unit
                                    exists, this Product Unit should not be set as the default. To change the
                                    default Unit, edit the existing one.
                                </span>
                            </ReactTooltip>
                        </span>
                        <DisabledInput source="parent_default_unit.id" label="ID"
                            style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                        <DisabledInput source="parent_default_unit.name" label="Name"
                            style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }} />
                    </DependentInput>

                    <hr style={viewStyles.break} />

                    <SelectInput source="unit_type" label="Unit Type"
                        validate={[required]}
                        choices={[
                            { id: 'Piece', name: 'Piece' },
                            { id: 'Mid', name: 'Mid' },
                            { id: 'Case', name: 'Case' }
                        ]}
                        style={{ ...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight }}
                    />

                    {/* Product Unit is current Default Piece */}
                    <DependentInput resolve={editDisplayIfPieceUnitAndAllowDefault}>
                        <SelectInput source="default_unit" label="Unit is Default"
                            validate={[required]}
                            choices={[
                                { id: true, name: 'Yes' },
                                { id: false, name: 'No' }
                            ]}
                            style={{ ...viewStyles.rowShowField, ...styles.rowShowField }} />
                    </DependentInput>

                    <DependentInput resolve={editDisplayIfPieceUnitAndDisallowDefault}>
                        <span style={viewStyles.fieldDescription}>Because there is a Default Piece Unit, this Unit cannot be set as the default unless it is the same Unit.</span>
                    </DependentInput>

                    <DependentInput dependsOn="unit_type" resolve={editDisplayIfNotPieceUnit}>
                        <TextInput source="piece_unit" label="Piece Unit" />
                        <span style={viewStyles.fieldDescription}>Piece Unit barcode for <em>Mid</em> and <em>Case</em> Unit Types.</span>
                    </DependentInput>

                    <DependentInput dependsOn="unit_type" resolve={editDisplayIfPieceUnit}>
                        <TextField source="units_per_scan_piece" value={1} defaultValue={1} label="Units per Scan" />
                    </DependentInput>

                    <DependentInput dependsOn="unit_type" resolve={editDisplayIfNotPieceUnit}>
                        <NumberInput source="units_per_scan" label="Units per Scan" defaultValue={1} step={1}
                            validate={[required, minValue(1, 'Must not be a negative number')]} />
                        <span style={viewStyles.fieldDescription}>
                            Enter the total of Units scanned at the register.
                            <TooltipIcon dataFor="ttUnitsPerScan" />
                            <ReactTooltip id="ttUnitsPerScan" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                The <strong>Units Per Scan</strong> sets how many pieces are included when
                                a <em>Mid</em> or a <em>Case</em> is scanned.
                            </ReactTooltip>
                        </span>
                    </DependentInput>

                    <SelectInput source="active" label="Display Product Unit on POS"
                        validate={[required]}
                        choices={[
                            { id: true, name: 'Yes' },
                            { id: false, name: 'No' }
                        ]} />
                    <span style={viewStyles.fieldDescription}>
                        Select Yes or No.
                        <TooltipIcon dataFor="ttActive" />
                        <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                            className="tooltip">
                            Selecting <em>Yes</em> will display the Product Unit on the POS with the ability to be sold.
                            <br />
                            Selecting <em>No</em> will remove the Product Unit from the POS so it can not be selected or sold.
                            <br /><br />
                            In some cases, you may want to create Product Units that will not be displayed because they are
                            not at your Location(s) yet. Also, a discontinued Product Unit can be removed from your
                            Location(s). This mitigates human error from having employees accidently sell Product Units
                            that are not in your Location(s).
                        </ReactTooltip>
                    </span>

                    <span style={viewStyles.fieldInstructions}>
                        Upon completing this Tab, click the Prices Tab to proceed.
                    </span>
                    <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
                </FormTab>

                {/* Edit Step: Prices - products_np */}
                {resolveAccess(permissions, [], ['products_np']) &&
                    <FormTab label="Prices" style={viewStyles.row}>
                        <DisabledInput source="taxable" label="Taxable" style={viewStyles.fieldLight} />

                        <TextField source="cost" label="Cost of Unit" style={viewStyles.fieldLight} />
                        <span style={viewStyles.fieldDescription}>
                            <strong>Cost</strong> to manufacture this Product Unit.
                            <TooltipIcon dataFor="ttCost" />
                            <ReactTooltip id="ttCost" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                <strong>Cost</strong> of Unit is for how much the Product Unit costs the business to manufacture
                                or purchase. This helps with determining certain values for Accounting Reports.
                            </ReactTooltip>
                        </span>

                        <TextField source="price.default" label="Default Price" type="number" style={viewStyles.fieldLight} />
                        <span style={viewStyles.fieldDescription}>
                            The <strong>Default Price</strong> for this Product Unit.
                            <TooltipIcon dataFor="ttDefaultPrice" />
                            <ReactTooltip id="ttDefaultPrice" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                This <strong>Default Price</strong> determines the selling Price of the Unit. Overrides may be
                                created for specific Locations or for specific Price Levels.
                            </ReactTooltip>
                        </span>

                        <DependentInput dependsOn="price.default" resolve={editDisplayPrices}>
                            {<MakePriceLevelPriceTable showButtons={{ 'create': false, 'delete': false }} />}
                            {<MakeLocationPriceTable showButtons={{ 'create': false, 'delete': false }} />}
                        </DependentInput>

                        <span style={viewStyles.fieldInstructions}>
                            After clicking <strong>Save</strong>, you will be redirected to this Product Unit's Product.
                        </span>
                        <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
                    </FormTab>
                }

                {/* Edit Step: Prices - products_all */}
                {resolveAccess(permissions, ['products_all']) &&
                    <FormTab label="Prices" style={viewStyles.row}>
                        <SelectInput source="taxable" label="Taxable"
                            validate={[required]}
                            choices={[
                                { id: true, name: 'Yes' },
                                { id: false, name: 'No' }
                            ]} />
                        <TextInput source="cost" label="Cost of Unit" />
                        <span style={viewStyles.fieldDescription}>
                            <strong>Cost</strong> to manufacture this Product Unit.
                            <TooltipIcon dataFor="ttCost" />
                            <ReactTooltip id="ttCost" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                <strong>Cost</strong> of Unit is for how much the Product Unit costs the business to manufacture
                                or purchase. This helps with determining certain values for Accounting Reports.
                            </ReactTooltip>
                        </span>
                        <TextInput source="price.default" label="Default Price" type="number"
                            validate={[required, minValue(0.01, 'Price must be above zero.')]} />
                        <span style={viewStyles.fieldDescription}>
                            The <strong>Default Price</strong> for this Product Unit.
                            <TooltipIcon dataFor="ttDefaultPrice" />
                            <ReactTooltip id="ttDefaultPrice" type="light" effect="solid" multiline={true} border={true}
                                className="tooltip">
                                This <strong>Default Price</strong> determines the selling Price of the Unit. Overrides may be
                                created for specific Locations or for specific Price Levels.
                            </ReactTooltip>
                        </span>

                        <DependentInput dependsOn="price.default" resolve={editDisplayPrices}>
                            {<MakePriceLevelPriceTable showButtons={{ 'create': true, 'delete': true }} />}
                            {<MakeLocationPriceTable showButtons={{ 'create': true, 'delete': true }} />}
                        </DependentInput>

                        <span style={viewStyles.fieldInstructions}>
                            After clicking <strong>Save</strong>, you will be redirected to this Product Unit's Product.
                        </span>
                        <hr style={{ ...viewStyles.break, ...viewStyles.paddingBottom }} />
                    </FormTab>
                }

            </TabbedForm>
        }
    </CreateEditProductUnit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/productUnit.js